<script setup lang="ts">
// Slides web: https://antoniandre.github.io/vueper-slides/#ex--show-multiple-slides-and-gap
import { VueperSlides, VueperSlide } from 'vueperslides'
import { useScreen } from '@/composables/useScreen'

const { isMobileView, screenWidth } = useScreen()

// There is no point to show all 57 specialisations. We are going to show only the most popular.
const visibleSpecialisations = [
  {
    id: 26,
    nameTranslation: 'protection_of_personal_honor_and_dignity',
    description: 'protection_of_personal_honor_and_dignity_description',
    slug: 'asmens-garbes-ir-orumo-gynimas'
  },
  {
    id: 3,
    nameTranslation: 'criminal_law',
    description: 'criminal_law_description',
    slug: 'baudziamoji-ir-baudziamojo-proceso-teise'
  },
  {
    id: 5,
    nameTranslation: 'labor_law',
    description: 'labor_law_description',
    slug: 'darbo-teise'
  },
  {
    id: 52,
    nameTranslation: 'property_rights',
    description: 'property_rights_description',
    slug: 'nuosavybes-teise'
  },
  {
    id: 10,
    nameTranslation: 'family_law',
    description: 'family_law_description',
    slug: 'seimos-teise'
  },
  {
    id: 42,
    nameTranslation: 'debt_recovery',
    description: 'debt_recovery_description',
    slug: 'skolu-isieskojimas'
  },
  {
    id: 46,
    nameTranslation: 'domestic_violence',
    description: 'domestic_violence_description',
    slug: 'smurto-artimoje-aplinkoje-teisiniai-klausimai-ir-procesas'
  },
  {
    id: 47,
    nameTranslation: 'contract_law',
    description: 'contract_law_description',
    slug: 'sutarciu-teise'
  }
]

const visibleSlidesCount = computed(() => {
  if (screenWidth.value > 1280) {
    return 4
  } else if (screenWidth.value > 975) {
    return 3
  } else if (screenWidth.value > 900) {
    return 2
  }

  return 1
})
const arrowShift = computed(() => 45 + (Math.ceil(visibleSpecialisations.length / visibleSlidesCount.value) * (isMobileView.value? 34 : 44) / 2))
const isAutoplayActive = computed(() => visibleSpecialisations.length / visibleSlidesCount.value > 1)
</script>
<template>
  <div>
    <div v-if="isMobileView">
      <vueper-slides
        class="no-shadow"
        :visible-slides="1"
        :gap="0"
        fixed-height="320px"
        :autoplay="isAutoplayActive"
        :touchable="true"
        :dragging-distance="10"
      >
        <template #bullet="{ active }">
          <div class="relative w-[25px] h-[4px] -mx-[6px] top-[44px] bg-darkBlue" :class="{'opacity-55' : !active}" />
        </template>
        <template #arrow-left>
          <div class="relative top-[178px]" :style="{left: `-${arrowShift}px`}">
            <IconArrowFullLeft class="text-darkBlue" />
          </div>
        </template>
        <template #arrow-right>
          <div class="relative top-[178px]" :style="{right: `-${arrowShift}px`}">
            <IconArrowFullRight class="text-darkBlue" />
          </div>
        </template>
        <vueper-slide
          v-for="(specialisation, index) in visibleSpecialisations"
          :key="index"
        >
          <template #content>
            <NuxtLinkLocale
              :to="{
                name: 'lawyers-specialty',
                params: {
                  specialty: $t('advocates').toLowerCase()
                },
                query: {
                  page: 1,
                  specialisation: specialisation.slug
                }
              }"
              class="flex items-center justify-center"
            >
              <div class="mobile-specialisation-container">
                <div class="ml-6 mr-6 text-darkBlue">
                  <div class="separator mt-8" />
                  <div class="mt-8 text-2xlg font-redhat font-semibold leading-tight">
                    {{ $t(specialisation.nameTranslation) }}
                  </div>
                  <div class="mt-3 text-md font-redhat leading-tight">
                    {{ $t(specialisation.description) }}
                  </div>
                </div>
              </div>
            </NuxtLinkLocale>
          </template>
        </vueper-slide>
      </vueper-slides>
    </div>
    <div v-else>
      <vueper-slides
        class="no-shadow"
        :visible-slides="visibleSlidesCount"
        slide-multiple
        :gap="0"
        fixed-height="260px"
        :autoplay="isAutoplayActive"
        :touchable="false"
      >
        <template #bullet="{ active }">
          <div class="relative mb-[18px] w-[35px] h-[4px] -mx-[6px] top-[31px] bg-darkBlue" :class="{'opacity-55' : !active}" />
        </template>
        <template #arrow-left>
          <div class="relative top-[187px]" :style="{left: `-${arrowShift}px`}">
            <IconArrowFullLeft class="text-darkBlue" />
          </div>
        </template>
        <template #arrow-right>
          <div class="relative top-[187px]" :style="{right: `-${arrowShift}px`}">
            <IconArrowFullRight class="text-darkBlue" />
          </div>
        </template>
        <vueper-slide
          v-for="(specialisation, index) in visibleSpecialisations"
          :key="index"
        >
          <template #content>
            <NuxtLinkLocale :to="{ name: 'lawyers-specialty', params: { specialty: $t('advocates').toLowerCase() }, query: { page: 1, specialisation: specialisation.slug }}" class="flex">
              <div class="specialisation-container">
                <div class="ml-6 mr-6 text-darkBlue">
                  <div class="separator mt-8" />
                  <div class="mt-6 text-2xlg font-redhat font-semibold leading-tight">
                    {{ $t(specialisation.nameTranslation) }}
                  </div>
                  <div class="mt-3 text-md font-branding">
                    {{ $t(specialisation.description) }}
                  </div>
                </div>
              </div>
            </NuxtLinkLocale>
          </template>
        </vueper-slide>
      </vueper-slides>
    </div>
  </div>
</template>
<style scoped>
.specialisation-container {
  width: 300px;
  height: 258px;
  background-color: white;
  border-radius: 40px;
  position: absolute;
  left: 20px;
}

.mobile-specialisation-container {
  width: 300px;
  height: 258px;
  background-color: white;
  border-radius: 40px;
}

.separator {
  width: 65px;
  border-bottom: 2px solid #3E5CF5;
}

:deep(.vueperslides__arrow) {
  opacity: 1;
}

:deep(.vueperslides__arrow--prev) {
  left: 50%;
}

:deep(.vueperslides__arrow--next) {
  right: 50%;
}
</style>
